import React from "react";

// A contentEditable rich-text editor that stores HTML and provides a toolbar
// with Bold, Italic, Underline, Numbered List, Bullet List, and Checkbox
// (subtask). Embedding links lives in its own "Embedded Links" section
// outside this editor — see EmbedLinksSection in CalendarExtras.jsx.
export const RichNotesEditor = React.memo(({ value = "", onChange, placeholder = "Add notes…", fontSize = 14.5 }) => {
  const editorRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  // Always call the latest onChange without re-rendering when it changes.
  const onChangeRef = React.useRef(onChange);
  React.useLayoutEffect(() => { onChangeRef.current = onChange; });

  const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"])/g;
  const linkifyEditor = () => {
    const root = editorRef.current;
    if (!root) return false;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const targets = [];
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentElement && node.parentElement.closest("a")) continue;
      URL_RE.lastIndex = 0;
      if (URL_RE.test(node.nodeValue)) targets.push(node);
    }
    if (!targets.length) return false;
    targets.forEach(textNode => {
      const text = textNode.nodeValue;
      const frag = document.createDocumentFragment();
      let last = 0, m;
      URL_RE.lastIndex = 0;
      while ((m = URL_RE.exec(text))) {
        const url = m[1];
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        const a = document.createElement("a");
        a.href = url;
        a.textContent = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.style.color = "#d4789a";
        a.style.fontWeight = "600";
        frag.appendChild(a);
        last = m.index + url.length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      textNode.parentNode.replaceChild(frag, textNode);
    });
    return true;
  };

  // Sync external value → DOM only on first mount (avoid caret jumps), then
  // linkify any URLs that were stored as plain text.
  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
    linkifyEditor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    clearTimeout(debounceRef.current);
    onChangeRef.current(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChangeRef.current(editorRef.current?.innerHTML || "");
    }, 600);
  };

  // On blur: linkify bare URLs, then flush the latest HTML immediately.
  const flushNow = () => {
    clearTimeout(debounceRef.current);
    linkifyEditor();
    onChangeRef.current(editorRef.current?.innerHTML || "");
  };

  // Force plain-text paste so URLs land as plain text (never as fragile
  // markup that a re-render could wipe). They become clickable links on blur.
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  // Checkbox/subtask row + click-to-toggle + Enter/Backspace behavior below all
  // mirror the Journal tab's checklist logic (same class-name pattern, same DOM
  // shape) — copied 1:1 so subtasks here behave exactly like Journal checklists.
  const handleEditorClick = (e) => {
    const box = e.target.closest && e.target.closest(".rn-check-box");
    if (box) {
      const label = box.parentElement && box.parentElement.querySelector(".rn-check-label");
      const checked = box.getAttribute("data-checked") === "1";
      if (checked) {
        box.removeAttribute("data-checked");
        box.textContent = "";
        box.style.background = "transparent";
        if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
      } else {
        box.setAttribute("data-checked", "1");
        box.textContent = "✓";
        box.style.background = "#d4789a";
        if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
      }
      clearTimeout(debounceRef.current);
      onChangeRef.current(editorRef.current?.innerHTML || "");
      return;
    }
    const anchor = e.target.closest("a[href]");
    if (anchor) {
      e.preventDefault();
      window.open(anchor.href, "_blank", "noopener,noreferrer");
    }
  };

  // Inserted with an empty label (no "Subtask" placeholder text) so the user can
  // start typing their own subtask text immediately.
  const insertCheckbox = () => {
    editorRef.current?.focus();
    const html = `<div class="rn-check" contenteditable="false" style="display:flex;align-items:flex-start;gap:8px;margin:4px 0 4px 4px"><span class="rn-check-box" style="width:16px;height:16px;border:2px solid #d4789a;border-radius:5px;flex-shrink:0;margin-top:2px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;line-height:1"></span><span class="rn-check-label" contenteditable="true" style="outline:none;flex:1;font-size:inherit;color:var(--c-text);line-height:1.55"><br></span></div><p><br></p>`;
    document.execCommand("insertHTML", false, html);
    clearTimeout(debounceRef.current);
    onChangeRef.current(editorRef.current?.innerHTML || "");
  };

  // Enter inside a subtask label continues to a new subtask row below (or, if
  // the label is empty, exits the checklist back to a normal line). Backspace
  // at the start of a subtask label drops its checkbox and keeps the text as a
  // plain line — the only way back to normal typing once a checkbox exists.
  const handleEditorKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const el = node.nodeType === 3 ? node.parentElement : node;
        const checkLabel = el && el.closest && el.closest(".rn-check-label");
        if (checkLabel) {
          e.preventDefault();
          const row = checkLabel.closest(".rn-check");
          if (checkLabel.textContent.trim() === "") {
            const p = document.createElement("div");
            p.innerHTML = "<br>";
            row.after(p);
            row.remove();
            const r2 = document.createRange();
            r2.setStart(p, 0);
            r2.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r2);
            clearTimeout(debounceRef.current);
            onChangeRef.current(editorRef.current?.innerHTML || "");
            return;
          }
          const newRow = document.createElement("div");
          newRow.className = "rn-check";
          newRow.contentEditable = "false";
          newRow.setAttribute("style", "display:flex;align-items:flex-start;gap:8px;margin:4px 0 4px 4px");
          const newBox = document.createElement("span");
          newBox.className = "rn-check-box";
          newBox.setAttribute("style", "width:16px;height:16px;border:2px solid #d4789a;border-radius:5px;flex-shrink:0;margin-top:2px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;line-height:1");
          const newLabel = document.createElement("span");
          newLabel.className = "rn-check-label";
          newLabel.contentEditable = "true";
          newLabel.setAttribute("style", "outline:none;flex:1;font-size:inherit;color:var(--c-text);line-height:1.55");
          newLabel.innerHTML = "<br>";
          newRow.appendChild(newBox);
          newRow.appendChild(newLabel);
          row.after(newRow);
          newLabel.focus();
          const r = document.createRange();
          r.setStart(newLabel, 0);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          clearTimeout(debounceRef.current);
          onChangeRef.current(editorRef.current?.innerHTML || "");
          return;
        }
      }
    }

    if (e.key !== "Backspace") return;
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const sc = range.startContainer;
    const el = sc.nodeType === 3 ? sc.parentElement : sc;

    const label = el && el.closest && el.closest(".rn-check-label");
    if (label && editorRef.current && editorRef.current.contains(label)) {
      const probe = range.cloneRange();
      probe.selectNodeContents(label);
      probe.setEnd(range.startContainer, range.startOffset);
      const atStart = probe.toString().length === 0;
      if (atStart) {
        e.preventDefault();
        const row = label.closest(".rn-check");
        const line = document.createElement("div");
        line.setAttribute("style", "line-height:1.55");
        while (label.firstChild) line.appendChild(label.firstChild);
        if (!line.firstChild) line.appendChild(document.createElement("br"));
        row.replaceWith(line);
        const r = document.createRange();
        r.setStart(line, 0);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        clearTimeout(debounceRef.current);
        onChangeRef.current(editorRef.current?.innerHTML || "");
        return;
      }
    }

    if (range.startOffset !== 0) return;
    let node = sc;
    while (node && node.parentNode !== editorRef.current) node = node.parentNode;
    const prev = node && node.previousSibling;
    if (prev && prev.classList && prev.classList.contains("rn-check")) {
      e.preventDefault();
      prev.remove();
      clearTimeout(debounceRef.current);
      onChangeRef.current(editorRef.current?.innerHTML || "");
    }
  };

  const tbBtn = (title, icon, action, isActive = false) => (
    <button
      key={title}
      title={title}
      className="rn-tb-btn"
      onMouseDown={e => { e.preventDefault(); action(); }}
      style={{
        width: 28, height: 28, border: "none", borderRadius: 6, cursor: "pointer",
        background: isActive ? "var(--c-bg-card)" : "transparent",
        color: isActive ? "#d4789a" : "#7a5070",
        fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0, transition: "background 0.12s",
      }}>{icon}</button>
  );

  return (
    <div className="rn-box" style={{ border: "1.5px solid #f0d0e8", borderRadius: 12, overflow: "visible", background: "var(--c-bg-card)", position: "relative" }}>
      {/* Toolbar */}
      <div className="rn-bar" style={{
        display: "flex", alignItems: "center", gap: 2, padding: "6px 8px",
        borderBottom: "1.5px solid #f0d0e8", background: "var(--c-bg-soft)", borderRadius: "12px 12px 0 0",
        flexWrap: "wrap",
      }}>
        {tbBtn("Bold", <b>B</b>, () => exec("bold"))}
        {tbBtn("Italic", <i>I</i>, () => exec("italic"))}
        {tbBtn("Underline", <u>U</u>, () => exec("underline"))}
        <div style={{ width: 1, height: 18, background: "var(--c-bg-active)", margin: "0 4px" }} />
        {tbBtn("Numbered List", "1≡", () => exec("insertOrderedList"))}
        {tbBtn("Bullet List", "•≡", () => exec("insertUnorderedList"))}
        {tbBtn("Checkbox / Subtask", "☑", insertCheckbox)}
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={flushNow}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onKeyDown={handleEditorKeyDown}
        style={{
          minHeight: 90, padding: "12px 16px", outline: "none",
          fontSize, fontWeight: 600, color: "var(--c-text)", lineHeight: 1.6,
          fontFamily: "'Quicksand','Nunito',sans-serif",
        }}
        data-placeholder={placeholder}
      />

      {/* Placeholder via CSS injected once. Ordered/unordered lists get their
          own left padding so markers sit clear of the editor's inner edge
          instead of hugging the container border. */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before{content:attr(data-placeholder);color:#c0a0b8;pointer-events:none;font-style:italic}
        [contenteditable] ol, [contenteditable] ul{padding-left:26px;margin:4px 0}
      `}</style>
    </div>
  );
// Never re-render due to value/onChange prop changes — the editor owns its DOM after mount.
}, (prev, next) => prev.placeholder === next.placeholder);
